// ═══ Audio Waveform Generator ═══
// Decodes audio file and extracts normalized peak data for timeline visualization.

/**
 * Decode an audio file and extract peak amplitude data.
 * Returns an array of normalized peaks (0-1) for drawing the waveform.
 */
export async function extractWaveformPeaks(
  audioUrl: string,
  targetPeaks: number = 2000
): Promise<number[]> {
  const response = await fetch(audioUrl)
  const arrayBuffer = await response.arrayBuffer()

  const audioContext = new AudioContext()
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

  // Mix down to mono
  const channelData = audioBuffer.getChannelData(0)
  const secondChannel = audioBuffer.numberOfChannels > 1
    ? audioBuffer.getChannelData(1)
    : null

  const mono = new Float32Array(channelData.length)
  for (let i = 0; i < channelData.length; i++) {
    mono[i] = secondChannel
      ? (channelData[i] + secondChannel[i]) / 2
      : channelData[i]
  }

  // Downsample to targetPeaks
  const samplesPerPeak = Math.floor(mono.length / targetPeaks)
  const peaks: number[] = []

  for (let i = 0; i < targetPeaks; i++) {
    const start = i * samplesPerPeak
    const end = Math.min(start + samplesPerPeak, mono.length)
    let max = 0
    for (let j = start; j < end; j++) {
      const abs = Math.abs(mono[j])
      if (abs > max) max = abs
    }
    peaks.push(max)
  }

  // Normalize to 0-1
  const globalMax = Math.max(...peaks, 0.001)
  const normalized = peaks.map(p => p / globalMax)

  await audioContext.close()
  return normalized
}

/**
 * Draw waveform peaks onto a canvas element.
 * Supports zoom and scroll offset for timeline integration.
 */
export function drawWaveform(
  canvas: HTMLCanvasElement,
  peaks: number[],
  options: {
    color?: string
    backgroundColor?: string
    barWidth?: number
    barGap?: number
    zoom?: number        // px per second
    durationMs?: number
    scrollLeft?: number
  } = {}
) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const {
    color = 'rgba(100, 160, 255, 0.6)',
    backgroundColor = 'transparent',
    barWidth = 2,
    barGap = 1,
    zoom = 60,
    durationMs = 0,
    scrollLeft = 0,
  } = options

  const { width, height } = canvas
  const dpr = window.devicePixelRatio || 1

  // Clear
  ctx.clearRect(0, 0, width, height)
  if (backgroundColor !== 'transparent') {
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
  }

  if (peaks.length === 0 || durationMs === 0) return

  // Calculate which peaks are visible
  const totalTimelinePx = (durationMs / 1000) * zoom
  const pxPerPeak = totalTimelinePx / peaks.length
  void (barWidth + barGap)

  const startPeak = Math.max(0, Math.floor(scrollLeft / pxPerPeak))
  const endPeak = Math.min(peaks.length, Math.ceil((scrollLeft + width / dpr) / pxPerPeak))

  ctx.fillStyle = color
  const centerY = height / 2

  for (let i = startPeak; i < endPeak; i++) {
    const x = (i * pxPerPeak - scrollLeft) * dpr
    const barHeight = peaks[i] * (height * 0.8)
    ctx.fillRect(
      Math.round(x),
      Math.round(centerY - barHeight / 2),
      Math.round(barWidth * dpr),
      Math.round(barHeight)
    )
  }
}
